/**
 * ATL-1377 — every "across all branches" figure on the product-stocks screen, over ONE definition
 * of which branches the organization actually has.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT
 *
 * A product document's `storeStock` is keyed by store id and KEEPS the key of a branch that has been
 * deleted. That is deliberate, not an oversight: `deleteStore` (global/Firebase/firebase.ts) prunes
 * `activeInStore.<storeId>` and `salePrices.<storeId>` and explicitly does NOT prune
 * `storeStock.<storeId>`, because removing a quantity with no `product_movements` row to say where
 * it went is the ATL-707 shape — "visible and wrong is better than invisible and wrong".
 *
 * So the stored map is right and the READER was wrong: the screen summed `storeStock` over its own
 * key set, which is "every branch this product has ever been stocked in", not "every branch this
 * business has". Measured on the reporting org (sPUDvCXSEg8HjdKqOmdY, product 356kY7KmsyRuQa3uKhVF):
 * four live branches summing to 33, plus `9omFMID70q7vGzbZ6LM8: -1` and `pquc0sX3tEfc5Q8HTkTA: 0`
 * for two branches that 404 — so the screen showed 32 while the only branch holding anything showed
 * 33. Fleet-wide at the time of the fix: 19,703 stock rows on deleted stores across 38 orgs, 1,989
 * of them non-zero and 152 negative.
 *
 * It also made the documented repair impossible. «Нашли ошибку в отчете?» → «Новое значение» sets
 * one BRANCH's stock, and no branch the merchant can select is the one dragging the total down.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE RULE, AND WHY IT LIVES HERE ONCE
 *
 * The organization's branches are `storesDictAtom` — nothing else. It is the same answer in both
 * read modes, which is what makes this fix mode-independent and the behaviour identical after the
 * Supabase cutover:
 *
 *   • Firestore mode — `db.collection('stores')` (global/AppStack.js). `deleteStore` HARD-deletes
 *     the document, so a deleted branch is simply absent.
 *   • Supabase mode  — `GET /stores` via `fetchStoresFromSupabase`, keyed by `firestoreId` (the
 *     same key space as `storeStock`). `StoresService.getStores` filters on the shared liveness
 *     predicate (`LIVE_STORE_IS_DELETED_FILTER` + `deleted_at IS NULL`), so the migration's
 *     `[Deleted Store]` FK placeholders — which carry `is_deleted: true`, NO `deleted_at`, and a
 *     REAL `firestore_id`, exactly what both of this ticket's ghosts are — are excluded there too.
 *
 * ATL-1023 is the reason all three aggregations below share `organizationStoreIds` rather than each
 * spelling the question out: two readers holding their own copy of "is this store live" is how they
 * come to disagree, and that has already cost this codebase a fleet of false cutover blockers.
 *
 * ⚠️ ONE ASYMMETRY IS DELIBERATE AND STAYS. The TOTAL counts every live branch; the VALUATION and
 * the SCORE count only the live branches the product is `activeInStore`. So a product deactivated
 * in a live branch that still physically holds stock reports those units in «Всего» and values them
 * at 0. Both halves predate this ticket and each is defensible on its own — the units exist, and a
 * branch that does not sell the product has nothing to reorder — but they can disagree on one row,
 * so the choice is recorded here rather than left to be rediscovered. It is NOT folded into the
 * rule above, because either way of forcing agreement is worse than the disagreement: gating the
 * total on `activeInStore` would HIDE real stock (the failure mode this codebase keeps having to
 * undo), and removing the gate from the valuation would move money figures that nothing in this
 * ticket is about.
 *
 * ⛔ NOT A DATA REPAIR. Nothing here writes. The stored `storeStock` key stays exactly where
 * ATL-911 left it; this module only stops the screen from adding it up.
 *
 * ⚠️ An EMPTY `storesDict` would make every figure here 0, which is the fabricated zero this
 * codebase keeps having to remove. It is unreachable on the calling screen rather than guarded:
 * the "all branches" mode only exists behind `StoreChooserHorizontal`, which renders nothing until
 * `storesDict` holds MORE THAN ONE store, and `stores` is `{critical: true, emptyIsFatal: true}` in
 * global/Supabase/initialLoad.js while AppStack deliberately never blanks a loaded atom on a
 * degraded refetch. If a caller ever reaches these helpers without a store list, it has a bigger
 * problem than the total.
 */

/**
 * The store ids an organization-wide figure may count — i.e. the branches the business still has.
 *
 * THE definition, exported so the callers below and any future one cannot drift from each other.
 *
 * @param {Object|null|undefined} storesDict keyed by store id, in the same id space as `storeStock`.
 * @returns {string[]}
 */
export const organizationStoreIds = (storesDict) => Object.keys(storesDict || {})

/**
 * One branch's stock cell, and WHETHER IT WAS READABLE AT ALL.
 *
 * `Number()` alone is too generous for a document this old: `Number(true)` is 1, `Number([7])` is 7
 * and `Number(new Date())` is an epoch in milliseconds, so a bare coercion reports each of them as a
 * stock level and adds it to the merchant's total. Only a number, or a string that parses to a
 * finite one, is a quantity — the same rule `readStockCell` applies in screens/Stats/
 * productStockAmounts.js, for the same reason.
 *
 * The `known` flag is not decoration, and collapsing it to a bare 0 is a real defect: 0 is the safe
 * reading for a TOTAL and a VALUATION (an unknown cell contributes nothing), and the UNSAFE one for
 * a SHORTAGE, where zero stock is the largest shortage a branch can have. A product document
 * holding `storeStock: {<liveStore>: 'NaN'}` would otherwise score as maximally out of stock and
 * push its supplier to the top of the reorder list on data nobody has — which is exactly what an
 * ABSENT cell is refused for two functions below. Unreadable and absent get the same answer.
 *
 * @param {unknown} raw
 * @returns {{known: boolean, quantity: number}}
 */
const readQuantity = (raw) => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { known: true, quantity: raw } : { known: false, quantity: 0 }
  }
  if (typeof raw !== 'string' || raw.trim() === '') return { known: false, quantity: 0 }
  const quantity = Number(raw)
  return Number.isFinite(quantity) ? { known: true, quantity } : { known: false, quantity: 0 }
}

/**
 * A product's stock across every branch the organization still has — the «Всего» figure.
 *
 * A live branch the product document has no cell for counts as 0, which is what the per-branch
 * display already shows for it.
 *
 * @param {Object|null|undefined} storeStock the product document's `storeStock` map.
 * @param {Object|null|undefined} storesDict `storesDictAtom`.
 * @returns {number}
 */
export function sumProductStockForStores (storeStock, storesDict) {
  return organizationStoreIds(storesDict).reduce(
    (total, storeId) => total + readQuantity(storeStock?.[storeId]).quantity,
    0
  )
}

/**
 * How many of the organization's branches this product is actually sold in.
 *
 * The "all branches" row colours itself by comparing the total against `stockThreshold` scaled by
 * this count — a per-branch threshold has to be multiplied by the number of branches before it can
 * be compared against a sum. Counting `activeInStore` keys directly overcounts by every key naming
 * a branch that no longer exists (ATL-1391: 20,229 products across 35 orgs still carry one, and on
 * the product that produced this ticket a deleted branch reads `true`), so a fully-stocked product
 * is measured against a threshold for more branches than the business has and renders as low stock.
 *
 * That would have been the ATL-1377 defect surviving in the colour after the number was fixed —
 * the row showing a corrected total, in red, for the wrong reason.
 *
 * @param {Object} product
 * @param {Object|null|undefined} storesDict `storesDictAtom`.
 * @returns {number}
 */
export function countActiveStoresForProduct (product, storesDict) {
  return organizationStoreIds(storesDict).filter((storeId) => product?.activeInStore?.[storeId]).length
}

/**
 * Purchase- and sale-price valuation of a product's stock across the branches it is active in.
 *
 * Same narrowing as the total, and for a sharper reason: `activeInStore` cannot be relied on to
 * exclude a deleted branch by itself. ATL-911 prunes that key going forward, but the 3,748 keys
 * orphaned across 22 orgs BEFORE it shipped are still in the documents — on the product that
 * produced this ticket, a deleted branch reads `activeInStore: true`. This is money on a supplier
 * row, so it is narrowed by the store list like everything else here.
 *
 * @param {Object} product
 * @param {Object|null|undefined} storesDict `storesDictAtom`.
 * @returns {{purchaseAmount: number, saleAmount: number}}
 */
export function valueProductStockForStores (product, storesDict) {
  let purchaseAmount = 0
  let saleAmount = 0
  const purchasePrice = Number(product?.purchasePrice) || 0
  for (const storeId of organizationStoreIds(storesDict)) {
    if (!product?.activeInStore?.[storeId]) continue
    const { quantity } = readQuantity(product?.storeStock?.[storeId])
    purchaseAmount += quantity * purchasePrice
    saleAmount += quantity * (Number(product?.salePrices?.[storeId]) || 0)
  }
  return { purchaseAmount, saleAmount }
}

/**
 * How urgently a product needs reordering, summed over the branches it is active in.
 *
 * Feeds the supplier ordering of the product-stocks screen. A deleted branch left at a negative
 * stock — the very shape this ticket is about — otherwise scores as the most urgent shortage the
 * supplier has, and pushes real shortages down the list.
 *
 * A branch whose stock cell is absent OR unreadable is NOT a shortage: every such cell compared
 * false in the loop this replaces (`undefined < t`, `'NaN' < t` and `{} < t` are all false, via a
 * NaN comparison), and inventing a shortage out of a cell nobody can read would reorder the screen
 * on data nobody has. See `readQuantity` — this is the one caller for which 0 is the dangerous
 * default rather than the safe one.
 *
 * @param {Object} product
 * @param {Object|null|undefined} storesDict `storesDictAtom`.
 * @returns {number}
 */
export function productRestockScore (product, storesDict) {
  const threshold = product?.stockThreshold || 1
  const importance = product?.importance != null ? product.importance : 1
  let score = 0
  for (const storeId of organizationStoreIds(storesDict)) {
    if (!product?.activeInStore?.[storeId]) continue
    const { known, quantity } = readQuantity(product?.storeStock?.[storeId])
    if (!known) continue
    if (quantity < threshold) score += importance * (threshold - quantity)
  }
  return score
}
